import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sliders, Sparkle, CaretDown, ArrowClockwise, Spinner } from '@phosphor-icons/react';

/**
 * RegenerateWithTweaks
 * --------------------
 * Collapsible panel that sits above the live preview and lets the user
 * tweak a few targeted fields (headline tone, CTA, price, offer, audience,
 * extra instruction) and regenerate the asset in-place.
 *
 * Philosophy:
 *   - No full form re-entry: current inputs are pre-filled and carried
 *     through to the regenerate call.
 *   - Reuses the same generate endpoint with the same payload shape +
 *     two new optional fields: headline_tone, tweak_notes.
 *   - Shows spinner while loading. On error the caller keeps the old
 *     preview and shows a toast.
 *
 * Props:
 *   fields: object  — the current payload (pre-filled into the inputs).
 *                     Must include any keys listed in `editable`.
 *   editable: array — list of field keys to show. E.g. ['offer','price','cta',
 *                     'target_customer'] for Sales Page, or ['title','cta',
 *                     'target_audience'] for Lead Magnet.
 *   onRegenerate: async (updatedFields) => void — called with the merged
 *                     payload when the user clicks Regenerate.
 *   loading: boolean — true while parent is regenerating.
 *   testIdPrefix: string — prefix for data-testid attributes (default 'tweaks').
 */

const FIELD_LABELS = {
  offer: 'Offer Name / Title',
  title: 'Title / Headline',
  price: 'Price',
  cta: 'CTA Wording',
  target_customer: 'Audience Wording',
  target_audience: 'Audience Wording',
};

const FIELD_PLACEHOLDERS = {
  offer: 'e.g., 7-Day Clean House Reset',
  title: 'e.g., The 30-Day Booking Blueprint',
  price: 'e.g., $299',
  cta: 'e.g., Book My Reset',
  target_customer: 'e.g., Busy urban professionals 30-45',
  target_audience: 'e.g., Busy urban professionals 30-45',
};

const TONE_OPTIONS = [
  { value: '', label: 'Default (no change)' },
  { value: 'more bold', label: 'More bold' },
  { value: 'more premium / luxury', label: 'More premium / luxury' },
  { value: 'more emotional', label: 'More emotional' },
  { value: 'simpler / beginner-friendly', label: 'Simpler / beginner-friendly' },
  { value: 'more direct', label: 'More direct' },
  { value: 'more high-ticket', label: 'More high-ticket' },
];

export default function RegenerateWithTweaks({
  fields = {},
  editable = [],
  onRegenerate,
  loading = false,
  testIdPrefix = 'tweaks',
}) {
  const [open, setOpen] = useState(false);
  const [local, setLocal] = useState(() => ({ ...fields }));
  const [headlineTone, setHeadlineTone] = useState('');
  const [tweakNotes, setTweakNotes] = useState('');

  const update = (key, value) => setLocal((prev) => ({ ...prev, [key]: value }));

  const handleRegenerate = async () => {
    const payload = {
      ...fields,
      ...local,
      headline_tone: headlineTone,
      tweak_notes: tweakNotes.trim(),
    };
    await onRegenerate(payload);
    // keep the panel open so the user sees the tweak that produced the result
  };

  return (
    <div
      className="bg-gradient-to-br from-blue-500/10 to-purple-500/10 border border-blue-500/30 rounded-xl p-3 sm:p-4"
      data-testid={`${testIdPrefix}-container`}
    >
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between text-left"
        data-testid={`${testIdPrefix}-toggle-btn`}
        type="button"
      >
        <div className="flex items-center space-x-2 text-white font-medium">
          <Sliders size={16} className="text-blue-300" />
          <span>Regenerate with Tweaks</span>
          <span className="text-xs font-normal text-blue-200/70 hidden sm:inline">
            — tweak a few fields and regenerate without starting over
          </span>
        </div>
        <CaretDown
          size={16}
          className={`text-blue-300 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="mt-4 space-y-3">
              {/* Tone picker */}
              <div>
                <label className="text-xs font-medium text-blue-100 mb-1 block">
                  Headline Tone
                </label>
                <select
                  value={headlineTone}
                  onChange={(e) => setHeadlineTone(e.target.value)}
                  className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-blue-400"
                  data-testid={`${testIdPrefix}-headline-tone-select`}
                >
                  {TONE_OPTIONS.map((t) => (
                    <option key={t.value} value={t.value} className="bg-slate-900 text-white">
                      {t.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Editable pre-filled fields */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {editable.map((key) => (
                  <div key={key}>
                    <label className="text-xs font-medium text-blue-100 mb-1 block">
                      {FIELD_LABELS[key] || key}
                    </label>
                    <input
                      type="text"
                      value={local[key] || ''}
                      onChange={(e) => update(key, e.target.value)}
                      placeholder={FIELD_PLACEHOLDERS[key] || ''}
                      className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm placeholder-white/40 focus:outline-none focus:border-blue-400"
                      data-testid={`${testIdPrefix}-${key}-input`}
                    />
                  </div>
                ))}
              </div>

              {/* Extra notes */}
              <div>
                <label className="text-xs font-medium text-blue-100 mb-1 block">
                  Extra Instruction <span className="text-blue-200/60">(optional)</span>
                </label>
                <textarea
                  rows={2}
                  value={tweakNotes}
                  onChange={(e) => setTweakNotes(e.target.value)}
                  placeholder="e.g., make it feel more luxury for busy working moms"
                  className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm placeholder-white/40 focus:outline-none focus:border-blue-400 resize-none"
                  data-testid={`${testIdPrefix}-notes-textarea`}
                />
              </div>

              {/* Regenerate button */}
              <button
                onClick={handleRegenerate}
                disabled={loading}
                type="button"
                className="w-full py-2.5 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold rounded-lg flex items-center justify-center space-x-2 transition-colors"
                data-testid={`${testIdPrefix}-regenerate-btn`}
              >
                {loading ? (
                  <>
                    <Spinner size={16} className="animate-spin" />
                    <span>Regenerating…</span>
                  </>
                ) : (
                  <>
                    <ArrowClockwise size={16} weight="bold" />
                    <span>Regenerate</span>
                    <Sparkle size={14} className="text-yellow-200" weight="fill" />
                  </>
                )}
              </button>
              <p className="text-xs text-blue-200/60 text-center">
                Your business context and current settings are preserved.
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
